import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ClockIcon, 
  PlayIcon, 
  CheckCircleIcon, 
  XCircleIcon, 
  ExclamationTriangleIcon,
  ChevronRightIcon,
  HomeIcon,
  CircleStackIcon,
  XMarkIcon
} from '@heroicons/react/24/outline';
import { Job, JobStatus, DynamicTableData } from '../../types';
import { ApiService } from '../../services/api';
import ProfilingReport from '../ProfilingReport';
import { useProject } from '../../contexts/ProjectContext';
import DAPTable from '../DAPTable/DAPTable';

interface JobMonitoringProps {
  isActive: boolean;
}

// JobWithFiles interface is no longer needed since Job interface now includes file names
// Keeping it for backward compatibility but it's identical to Job now
interface JobWithFiles extends Job {}

const JobMonitoring: React.FC<JobMonitoringProps> = ({ isActive }) => {
  const navigate = useNavigate();
  const { selectedProject } = useProject();
  const [runningJobs, setRunningJobs] = useState<JobWithFiles[]>([]);
  const [queuedJobs, setQueuedJobs] = useState<JobWithFiles[]>([]);
  const [waitingForApprovalJobs, setWaitingForApprovalJobs] = useState<JobWithFiles[]>([]);
  const [completedJobs, setCompletedJobs] = useState<JobWithFiles[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [showProfilingReport, setShowProfilingReport] = useState(false);
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);
  
  // Dynamic table data modal state
  const [showDynamicTableModal, setShowDynamicTableModal] = useState(false);
  const [dynamicTableData, setDynamicTableData] = useState<DynamicTableData | null>(null);
  const [dynamicTableLoading, setDynamicTableLoading] = useState(false);
  const [dynamicTableError, setDynamicTableError] = useState<string | null>(null);
  const [selectedDataFileId, setSelectedDataFileId] = useState<number | null>(null);
  const [selectedDataFileName, setSelectedDataFileName] = useState<string | null>(null);

  // Fetch all jobs and categorize them
  const fetchJobs = async () => {
    try {
      setLoading(true);
      const pid = selectedProject?.id;
      const response = await ApiService.getJobs(undefined, undefined, 0, 100, true, pid);
      const jobs = response.data;

      // Categorize jobs
      const running = jobs.filter(job => job.status === 'STARTED');
      const queued = jobs.filter(job => job.status === 'INQUEUE');
      const waitingForApproval = jobs.filter(job => job.status === 'WAITING_FOR_APPROVAL');
      const completed = jobs.filter(job => 
        job.status === 'COMPLETED' || 
        job.status === 'FAILED' || 
        job.status === 'TERMINATED'
      );

      setRunningJobs(running);
      setQueuedJobs(queued);
      setWaitingForApprovalJobs(waitingForApproval);
      setCompletedJobs(completed);
      setError(null);
    } catch (err) {
      console.error("Error fetching workflows:", err);
      setError("Failed to load workflows. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  // Fetch jobs when component is active
  useEffect(() => {
    if (isActive) {
      fetchJobs();
    }
  }, [isActive, selectedProject]);

  // Additional polling every 10 seconds to ensure we catch all transitions
  useEffect(() => {
    if (!isActive) return;

    const interval = setInterval(() => {
      fetchJobs();
    }, 10000);

    return () => clearInterval(interval);
  }, [isActive]);

  const getJobStatusColor = (status: JobStatus) => {
    switch (status) {
      case 'INQUEUE':
        return 'text-yellow-600 bg-yellow-100 border-yellow-200';
      case 'STARTED':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'WAITING_FOR_APPROVAL':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      case 'COMPLETED':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'FAILED':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'TERMINATED':
        return 'text-gray-600 bg-gray-100 border-gray-200';
      case 'TIMEOUT':
        return 'text-orange-600 bg-orange-100 border-orange-200';
      default:
        return 'text-gray-600 bg-gray-100 border-gray-200';
    }
  };

  const getStageStatusColor = (status: string) => {
    switch (status) {
      case 'INPROGRESS':
        return 'text-blue-600 bg-blue-100 border-blue-200';
      case 'COMPLETED':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'FAILED':
        return 'text-red-600 bg-red-100 border-red-200';
      case 'APPROVED':
        return 'text-green-600 bg-green-100 border-green-200';
      case 'REJECTED':
        return 'text-red-600 bg-red-100 border-red-200';
      default:
        return 'text-gray-400 bg-gray-50 border-gray-200';
    }
  };

  const getStageIcon = (stageName: string, status: string) => {
    const isActive = status !== 'INACTIVE';
    const iconClass = `w-5 h-5 ${isActive ? '' : 'text-gray-400'}`;

    switch (stageName) {
      case 'profiling':
        return <ClockIcon className={iconClass} />;
      case 'correction_recommendation':
        return <PlayIcon className={iconClass} />;
      default:
        return <ClockIcon className={iconClass} />;
    }
  };

  const calculateDuration = (startedAt: string, endedAt?: string) => {
    const start = new Date(startedAt);
    const end = endedAt ? new Date(endedAt) : new Date();
    const diffMs = end.getTime() - start.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffSecs = Math.floor((diffMs % 60000) / 1000);
    
    if (diffMins > 0) {
      return `${diffMins}m ${diffSecs}s`;
    }
    return `${diffSecs}s`;
  };

  const handleStageClick = (jobId: number, stageName: string, status: string) => {
    // Handle data_persistence stage click
//     if (stageName === 'data_persistence' && status !== 'INACTIVE') {
//       console.log('Data persistence click detected', { jobId, stageName, status });
//
//       // Find the job to get the data file name
//       const job = [...runningJobs, ...queuedJobs, ...waitingForApprovalJobs, ...completedJobs]
//         .find(j => j.id === jobId);
//
//       console.log('Found job:', job);
//
//       if (job) {
//         setSelectedDataFileId(job.data_file_id);
//
//         if (!job.data_file_name) {
//           console.error('Data file name not available for job:', job);
//           setDynamicTableError('Data file name not available. Please try again.');
//           setShowDynamicTableModal(true);
//           return;
//         }
//
//         console.log('Using data file name:', job.data_file_name);
//
//         // Use a fallback to mock data if API calls fail
//         try {
//           fetchDynamicTableData(job.data_file_id, job.data_file_name);
//         } catch (error) {
//           console.error('Error fetching dynamic table data:', error);
//
//           // Strip file extension for table name in mock data
//           const tableName = job.data_file_name.replace(/\.[^/.]+$/, "");
//
//           // Fallback to mock data if API call fails
//           const mockData = {
//             table_name: tableName,
//             schema: "dmp_app",
//             columns: ["id", "column1", "column2", "column3"],
//             data: [
//               { id: 1, column1: "Value 1", column2: "Value 2", column3: "Value 3" },
//               { id: 2, column1: "Value 4", column2: "Value 5", column3: "Value 6" },
//               { id: 3, column1: "Value 7", column2: "Value 8", column3: "Value 9" }
//             ],
//             total_rows: 3,
//             page: 1,
//             page_size: 100,
//             total_pages: 1
//           };
//
//           setDynamicTableData(mockData);
//           setShowDynamicTableModal(true);
//         }
//       } else {
//         console.error('Job not found for ID:', jobId);
//         setDynamicTableError('Job not found. Please try again.');
//         setShowDynamicTableModal(true);
//       }
//       return;
//     }
    
    if (stageName === 'profiling') {
      setShowProfilingReport(true);
      setSelectedJobId(jobId);
      return;
    }
    
    // Allow clicking on correction_recommendation stage when it's completed, failed, approved, or rejected
    if (stageName === 'correction_recommendation' && (status === 'COMPLETED' || status === 'FAILED' || status === 'APPROVED' || status === 'REJECTED')) {
      navigate(`/correction-recommendation/${jobId}`);
      return;
    }
    
    // For other stages, show coming soon alert (only if not inactive)
    if (status !== 'INACTIVE') {
      alert(`${stageName.charAt(0).toUpperCase() + stageName.slice(1)} details page - Coming Soon!`);
    }
  };

  // Function to fetch dynamic table data
  const fetchDynamicTableData = async (dataFileId: number, dataFileName: string) => {
    console.log('fetchDynamicTableData called with dataFileId:', dataFileId, 'dataFileName:', dataFileName);
    try {
      setDynamicTableLoading(true);
      setDynamicTableError(null);
      
      // Strip file extension from the file name to use as table name
      const tableName = dataFileName.replace(/\.[^/.]+$/, "");
      console.log('Using table name (without extension):', tableName);
      
      // Check if table exists
      console.log('Checking if table exists...');
      const exists = await ApiService.checkDynamicTableExists(tableName);
      console.log('Table exists:', exists);
      
      if (!exists) {
        console.error(`No dynamic table found for file "${dataFileName}" (table name: "${tableName}")`);
        setDynamicTableError(`No dynamic table found for file "${dataFileName}". The table may not have been created yet.`);
        
        // Show the modal even with error to provide feedback to the user
        setShowDynamicTableModal(true);
        setDynamicTableLoading(false);
        return;
      }
      
      // Fetch table data
      console.log('Fetching table data...');
      const data = await ApiService.getDynamicTableData(tableName);
      console.log('Received table data:', data);
      
      if (!data || !data.columns || !data.data) {
        throw new Error('Invalid data format received from API');
      }
      
      setDynamicTableData(data);
      console.log('Setting showDynamicTableModal to true');
      setShowDynamicTableModal(true);
    } catch (error) {
      console.error("Error fetching dynamic table data:", error);
      setDynamicTableError(`Failed to fetch dynamic table data for file "${dataFileName}". Please try again.`);
      
      // Show the modal even with error to provide feedback to the user
      setShowDynamicTableModal(true);
    } finally {
      setDynamicTableLoading(false);
    }
  };

  // Function to close dynamic table modal
  const closeDynamicTableModal = () => {
    console.log('Closing modal, current state:', { showDynamicTableModal, dynamicTableData });
    setShowDynamicTableModal(false);
    // Add a small delay before clearing the data to ensure smooth transition
    setTimeout(() => {
      setDynamicTableData(null);
      setDynamicTableError(null);
      console.log('Modal state cleared');
    }, 300);
  };

  // Add useEffect to monitor state changes
  useEffect(() => {
    console.log('showDynamicTableModal state changed:', showDynamicTableModal);
    console.log('dynamicTableData state:', dynamicTableData);
  }, [showDynamicTableModal, dynamicTableData]);

  const renderJobCard = (job: JobWithFiles) => {
    // Define stages to display
    const stages = ['profiling', 'correction_recommendation'];

    return (
      <div key={job.id} className="bg-white rounded-lg shadow-sm border border-gray-200">
        {/* Compact Job Header with all essential info */}
        <div className="p-2 border-b border-gray-200 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <div className={`px-2 py-1 rounded-full text-xs font-medium ${getJobStatusColor(job.status)}`}>
              {job.status}
            </div>
            <h3 className="text-sm font-bold text-gray-900">Workflow #{job.id}</h3>
          </div>
          <div className="flex items-center gap-3 text-xs">
            <div>
              <span className="font-medium text-gray-500 mr-1">Started:</span>
              <span className="font-semibold text-gray-900">
                {new Date(job.started_at).toLocaleTimeString([], {hour: '2-digit', minute:'2-digit'})}
              </span>
            </div>
            <div>
              <span className="font-medium text-gray-500 mr-1">Duration:</span>
              <span className="font-semibold text-gray-900">
                {calculateDuration(job.started_at, job.ended_at)}
              </span>
            </div>
            <span className="text-gray-500 ml-2">{job.started_by}</span>
          </div>
        </div>

        {/* Combined File Info and Stages - Horizontal layout */}
        <div className="p-2 border-b border-gray-200 flex items-center gap-4 text-xs">
          {/* Data File */}
          <div className="flex-1">
            <div className="font-medium text-gray-500">Data File</div>
            <div className="font-semibold text-gray-900 truncate" title={job.data_file_name || `File ID: ${job.data_file_id}`}>
              {job.data_file_name || `File ID: ${job.data_file_id}`}
            </div>
          </div>
          
          {/* Metadata File */}
          <div className="flex-1">
            <div className="font-medium text-gray-500">Metadata File</div>
            <div className="font-semibold text-gray-900 truncate" title={job.metadata_file_name || `File ID: ${job.metadata_file_id}`}>
              {job.metadata_file_name || `File ID: ${job.metadata_file_id}`}
            </div>
          </div>
          
          {/* Compact Stages */}
          <div className="flex gap-2 flex-1">
            {stages.map((stageName, index) => {
              const stage = job.stages?.find(s => s.stage_name === stageName);
              const status = stage?.status || 'INACTIVE';
              const isClickable = (stageName === 'profiling')
                ? (status === 'COMPLETED' || status === 'FAILED')
                : (stageName === 'correction_recommendation')
                ? (status === 'COMPLETED' || status === 'FAILED' || status === 'APPROVED' || status === 'REJECTED')
                : status !== 'INACTIVE';
              
              return (
                <button
                  key={stageName}
                  onClick={() => handleStageClick(job.id, stageName, status)}
                  disabled={!isClickable}
                  className={`
                    p-1 rounded border transition-all duration-200 text-left relative text-xs flex-1
                    ${isClickable 
                      ? `${getStageStatusColor(status)} hover:shadow-md cursor-pointer` 
                      : status !== 'INACTIVE' 
                        ? `${getStageStatusColor(status)} cursor-not-allowed`
                        : 'bg-gray-50 border-gray-200 cursor-not-allowed'
                    }
                  `}
                >
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-1">
                      {getStageIcon(stageName, status)}
                      <span className={`font-semibold truncate ${isClickable ? '' : status !== 'INACTIVE' ? '' : 'text-gray-400'}`}>
                        {stageName === 'profiling' ? 'Profiling' : 'Correction Recommendation'}
                      </span>
                    </div>
                    {isClickable && <ChevronRightIcon className="w-3 h-3" />}
                  </div>
                  
                  <div className="mt-0.5 flex items-center justify-between">
                    <div className={`font-medium text-xs ${isClickable ? '' : status !== 'INACTIVE' ? '' : 'text-gray-400'}`}>
                      {status === 'INACTIVE' ? 'Not Started' : status}
                    </div>
                    
                    {stage && stage.started_at && (
                      <div className="text-gray-500 text-xs">
                        {calculateDuration(stage.started_at, stage.ended_at)}
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </div>
    );
  };

  const renderSection = (title: string, jobs: JobWithFiles[], icon: React.ReactNode) => (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      {/* Section Header */}
      <div className="px-4 py-3 border-b border-gray-200 bg-gray-50 rounded-t-lg">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {icon}
            <h2 className="text-lg font-bold text-gray-900">{title}</h2>
          </div>
          <div className="text-xs font-medium text-gray-500">
            {jobs.length} {jobs.length === 1 ? 'workflow' : 'workflows'}
          </div>
        </div>
      </div>

      {/* Section Content */}
      <div className="p-3">
        {jobs.length > 0 ? (
          <div className="space-y-2.5">
            {jobs.map(job => renderJobCard(job))}
          </div>
        ) : (
          <div className="text-center py-6">
            <p className="text-gray-500">No workflows in this category</p>
          </div>
        )}
      </div>
    </div>
  );

  // Dynamic Table Data Modal
  const renderDynamicTableModal = () => {
    console.log('Rendering modal, showDynamicTableModal:', showDynamicTableModal);
    
    if (!showDynamicTableModal) {
      console.log('Modal is not visible, returning null');
      return null;
    }

    console.log('Modal should be visible, rendering content');
    return (
      <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
        <div className="bg-white rounded-lg shadow-xl w-11/12 max-w-6xl max-h-[90vh] flex flex-col">
          {/* Modal Header */}
          <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center">
            <h3 className="text-xl font-bold text-gray-900">
              {dynamicTableData ? `Table Data: ${dynamicTableData.table_name}` : 'Table Data'}
            </h3>
            <button 
              onClick={() => {
                console.log('Close button clicked');
                closeDynamicTableModal();
              }}
              className="text-gray-400 hover:text-gray-500 focus:outline-none"
            >
              <XMarkIcon className="w-6 h-6" />
            </button>
          </div>

          {/* Modal Content */}
          <div className="flex-1 p-6 overflow-auto">
            {dynamicTableLoading && (
              <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
              </div>
            )}

            {dynamicTableError && (
              <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
                <p>{dynamicTableError}</p>
              </div>
            )}

            {!dynamicTableLoading && !dynamicTableError && dynamicTableData && (
              <div>
                <div className="mb-4 flex justify-between items-center">
                  <div>
                    <span className="text-sm text-gray-500 mr-2">Total Rows:</span>
                    <span className="text-sm font-medium">{dynamicTableData.total_rows}</span>
                  </div>
                  <div>
                    <span className="text-sm text-gray-500 mr-2">Page:</span>
                    <span className="text-sm font-medium">{dynamicTableData.page} of {dynamicTableData.total_pages}</span>
                  </div>
                </div>

                <DAPTable
                  columns={dynamicTableData.columns.map(col => ({
                    key: col,
                    label: col.charAt(0).toUpperCase() + col.slice(1).replace('_', ' '),
                    align: 'left'
                  }))}
                  data={dynamicTableData.data}
                  title={`Data File ${selectedDataFileName} Table`}
                  emptyMessage="No data available"
                  striped={true}
                  hoverable={true}
                />
              </div>
            )}
          </div>

          {/* Modal Footer */}
          <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
            <button
              onClick={() => {
                console.log('Close button clicked in footer');
                closeDynamicTableModal();
              }}
              className="px-4 py-2 bg-gray-200 text-gray-800 rounded hover:bg-gray-300 focus:outline-none"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="p-3">
      <div className="mb-4">
        <h1 className="text-2xl font-bold text-gray-900">Workflows Monitoring</h1>
        <p className="text-gray-500 mt-1">
          Monitor and manage your data processing Workflows
        </p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded mb-4 text-sm">
          <p>{error}</p>
        </div>
      )}

      <div className="space-y-4">
        {/* Running Jobs Section */}
        {runningJobs.length > 0 && (
          renderSection("Running Workflows", runningJobs, <ClockIcon className="w-6 h-6 text-blue-500" />)
        )}

        {/* Queued Jobs Section */}
        {queuedJobs.length > 0 && (
          renderSection("Queued Workflows", queuedJobs, <ClockIcon className="w-6 h-6 text-yellow-500" />)
        )}

        {/* Waiting for Approval Jobs Section */}
        {waitingForApprovalJobs.length > 0 && (
          renderSection("Waiting for Approval", waitingForApprovalJobs, <ExclamationTriangleIcon className="w-6 h-6 text-orange-500" />)
        )}

        {/* Completed Jobs Section */}
        {renderSection("Completed Workflows", completedJobs, <CheckCircleIcon className="w-6 h-6 text-green-500" />)}
      </div>

      {/* Profiling Report Modal */}
      {showProfilingReport && selectedJobId && (
        <ProfilingReport
          jobId={selectedJobId}
          onClose={() => {
            setShowProfilingReport(false);
            setSelectedJobId(null);
          }}
        />
      )}

      {/* Dynamic Table Data Modal */}
      {renderDynamicTableModal()}
    </div>
  );
};

export default JobMonitoring;
